import { useEffect } from "react";
import ArticleFooterCTA from "../components/ArticleFooterCTA";

export default function Automation() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main
      data-testid="article-automation"
      className="px-6 md:px-12 lg:px-24"
    >
      <article className="max-w-3xl mx-auto pt-20 md:pt-32 pb-12 relative">
        <div
          aria-hidden="true"
          className="glow-orb orb-drift hidden md:block"
          style={{
            width: "420px",
            height: "420px",
            top: "-80px",
            right: "-180px",
            opacity: 0.35,
          }}
        />
        <header className="relative border-b border-subtle pb-12 mb-12">
          <span className="fade-up sticker sticker-tilt-left inline-flex">
            <span
              className="w-1.5 h-1.5 rounded-full pulse-dot"
              style={{ background: "var(--accent)" }}
            />
            Field Note · 02 · Systems
          </span>
          <h1 className="fade-up-1 mt-5 font-display text-primary text-4xl md:text-6xl font-light tracking-tight leading-[1.04]">
            The Visual Automation Blueprint
          </h1>
          <p className="fade-up-2 mt-6 text-secondary text-lg md:text-xl leading-relaxed max-w-2xl">
            The mental model behind visual workflows that quietly buy back 20+
            hours a week — without writing code, hiring a VA, or learning a new
            language.
          </p>
          <div className="fade-up-3 mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.22em] text-tertiary">
            <span>11 min read</span>
            <span>·</span>
            <span>For builders</span>
            <span>·</span>
            <span>Updated Feb 2026</span>
          </div>
        </header>

        <div className="prose-editorial fade-up-4">
          <p>
            Automation isn&rsquo;t about tools. It&rsquo;s about noticing the
            same five-minute task you keep doing on Tuesday. The tool is the
            cheap part. The expensive part is learning to see your own
            workflow from above.
          </p>

          <p>
            This is the model I use to find, design, and ship automations
            inside Make.com — the visual canvas that connects every app you
            already pay for. Same model works in Zapier, n8n, or Pipedream.
            The canvas is interchangeable. The thinking is not.
          </p>

          <blockquote>
            You don&rsquo;t automate tasks. You automate the seams between them.
          </blockquote>

          <h2>The Three-Question Audit</h2>
          <p>
            Before opening any automation tool, sit with a notepad for twenty
            minutes and answer three questions about your last week of work.
          </p>

          <ul>
            <li>
              <strong>What did I copy-paste more than twice?</strong> Every
              repeated copy-paste is a workflow asking to be automated.
            </li>
            <li>
              <strong>What did I check before doing?</strong> Every &ldquo;let me
              just see if X happened&rdquo; is a trigger waiting to fire.
            </li>
            <li>
              <strong>What did I forget?</strong> The thing you forgot is the
              thing you needed a notification for. That notification is a
              two-step scenario.
            </li>
          </ul>

          <p>
            Most solo operators end this audit with twelve candidate
            automations. You build the three with the highest weekly frequency
            and the lowest decision-making complexity. Save the rest for later.
          </p>

          <h2>The Anatomy of a Scenario</h2>
          <p>
            A visual automation in Make.com is just a chain of modules. Every
            useful scenario has the same five-part shape:
          </p>

          <ul>
            <li>
              <strong>Trigger</strong> — what starts it. A new row in Airtable,
              a tagged email, a form submission, a calendar event.
            </li>
            <li>
              <strong>Filter</strong> — what stops it from running on the wrong
              thing. The most important module nobody adds early enough.
            </li>
            <li>
              <strong>Enrich</strong> — what fetches the missing context.
              Usually a lookup, an HTTP call, or a quick AI prompt.
            </li>
            <li>
              <strong>Decide</strong> — a router or a conditional. Where the
              workflow forks based on what just came in.
            </li>
            <li>
              <strong>Act</strong> — the move you actually wanted to make. Send,
              create, update, notify, archive.
            </li>
          </ul>

          <p>
            When a scenario breaks, it&rsquo;s almost always missing a Filter
            step or a malformed Enrich. Master those two and you&rsquo;ll
            debug 80% of every workflow you&rsquo;ll ever build.
          </p>

          <h2>Five Scenarios That Earn Their Keep</h2>

          <h3>1 · The Inbox Triage</h3>
          <p>
            Trigger: email with a specific label. Enrich: AI summarises the
            thread into a 2-line digest. Act: drop it into your Notion Inbox
            with a link back. Saves the daily &ldquo;catching up on email&rdquo;
            tax — usually 45 minutes a day. I build all of these in Make.com (<a href="/go/make" className="text-accent hover:underline">/go/make</a>). It is the elite visual canvas that doesn't punish you with per-step pricing.
          </p>

          <h3>2 · The Client Onboarding</h3>
          <p>
            Trigger: new row in your CRM marked <code>Signed</code>. Act in
            sequence: create a folder in Drive, clone a Notion project page,
            generate a kickoff invite, draft a welcome email for review. One
            click instead of forty minutes.
          </p>

          <h3>3 · The Content Repurposer</h3>
          <p>
            Trigger: new long-form post published. Enrich: AI extracts five
            quote-pull candidates, three social hooks, one newsletter blurb.
            Act: drops the package into a Notion row tagged for review. You
            edit. You don&rsquo;t start from blank.
          </p>

          <h3>4 · The Weekly Numbers</h3>
          <p>
            Trigger: scheduled, every Friday 4pm. Act: pull totals from Stripe,
            sessions from analytics, new subscribers from your ESP. Drop a
            one-row summary into a Notion log. Three minutes saved becomes a
            three-year track record.
          </p>

          <h3>5 · The Quiet Notifier</h3>
          <p>
            Trigger: anything important you keep forgetting to check —
            cashflow dipping below a threshold, a client message older than 24
            hours, an invoice unpaid for 14 days. Act: one well-formatted
            message into your phone. That&rsquo;s the entire scenario. It will
            save you from one expensive miss per quarter. The scenario pays
            for itself the first time it fires.
          </p>

          <h2>The Build Order</h2>
          <p>
            New scenarios should be built in this exact sequence. Skip a step
            and you&rsquo;ll spend twice as long debugging.
          </p>

          <ul>
            <li>
              <strong>Sketch on paper first</strong>. Boxes and arrows. If you
              can&rsquo;t draw it in ninety seconds, it&rsquo;s too complex
              for v1.
            </li>
            <li>
              <strong>Build the trigger and one Act only</strong>. Confirm it
              fires. Don&rsquo;t add Filter or Enrich yet.
            </li>
            <li>
              <strong>Add Filter immediately</strong>. Before any logic, before
              any AI. Filters prevent runaway scenarios at 3am.
            </li>
            <li>
              <strong>Add Enrich one module at a time</strong>. Test after each
              one. Visual canvases lie about where they break.
            </li>
            <li>
              <strong>Wrap with an Error Handler</strong>. One Slack or email
              notification on any failure. You&rsquo;ll never wonder if it&rsquo;s
              running.
            </li>
          </ul>

          <h2>The 20-Hour Question</h2>
          <p>
            People ask if visual automation really buys back 20+ hours a week.
            The honest answer: not from any single scenario. It comes from the
            compound effect of fifteen small automations running quietly in
            the background while you focus on the work that actually moves the
            business.
          </p>

          <blockquote>
            Don&rsquo;t hunt for the one automation that changes everything.
            Build fifteen that each save twenty minutes.
          </blockquote>

          <h2>What This Buys You</h2>
          <p>
            A studio that runs whether you&rsquo;re at your desk or not. A
            calendar that defends itself. A nervous system that stops scanning
            for things to remember, because the systems remember on your
            behalf.
          </p>

          <p>
            Run the audit. Build three scenarios this month. Add Filter steps
            religiously. Let compound time do the rest.
          </p>
        </div>

        <ArticleFooterCTA testId="automation-article" />
      </article>
    </main>
  );
}
